import { usePreload } from "theme/modules/Router/usePreload";
import { useState, useCallback, useEffect } from "react";
import { useHistory } from "react-router";

export const useAutocompleteInput = ({
  isExpanded,
  selected,
  resultsElementId = "search-results",
}) => {
  return {
    autoComplete: "off",
    role: "combobox",
    "aria-autocomplete": "list",
    "aria-expanded": isExpanded,
    "aria-owns": resultsElementId,
    "aria-activedescendant": selected,
  };
};

// hack to prevent tsc from erroring at src/web/theme/components/organisms/Autocomplete/AutocompleteResults/AutocompleteOption.js:22
/**
 * @returns {TODO_TypeDef[]}
 */
export const useAutocompleteOption = (id, path, selected, onSelect) => {
  const isSelected = id === selected;

  return [
    isSelected,
    {
      role: "option",
      id: id,
      "aria-selected": id === selected,
      "data-value": path,
      onMouseEnter: () => onSelect(id),
    },
  ];
};

/**
 * Hook allowing to handle autocomplete common interactions
 * in an accessible way.
 *
 * @param {React.MutableRefObject} contentRef DOM element containing results???
 * @param {boolean} isOpen whether the autocomplete suggestions are opened or not
 * @param {React.Key} currentSearchKey a key to identify the current search
 *    (e.g: the user query)
 * @param {string} defaultSelectedId id of an option to be selected by default
 * @param {function} onClose callback to close the autocomplete suggestions
 * @param {function} [onOptionChosen] callback called when an option is chosen, with its value
 *    (default: redirect the user to the option's location)
 * @returns {[ string, function(string): void]} the current selected item id and a callback to set it
 */
const useAutocomplete = (
  contentRef,
  isOpen,
  currentSearchKey,
  defaultSelectedId,
  onClose,
  onOptionChosen
) => {
  const preload = usePreload();
  const [selected, select] = useState(null);

  const getOption = (contentRef, id) => {
    return contentRef.current && contentRef.current.querySelector(`#${id}`);
  };

  const getPathFromOption = (option) => {
    return option.dataset.value;
  };

  const isActive = (contentRef) => {
    return (
      contentRef.current &&
      document.activeElement ===
        contentRef.current.querySelector('[role="combobox"]')
    );
  };

  const onSelect = useCallback(
    (id) => {
      if (!id) {
        select(null);
        return;
      }

      const option = getOption(contentRef, id);
      if (isActive(contentRef) && option && selected !== id) {
        select(id);
        if (!onOptionChosen) {
          preload(getPathFromOption(option));
        }
      }
    },
    [contentRef, selected, preload, onOptionChosen]
  );

  const updateIndex = useCallback(
    (indexReducer, container, event) => {
      const options = container.querySelectorAll('[role="option"]');
      if (options.length === 0) {
        return;
      }
      let newIndex = 0;
      for (let index in options) {
        if (options[index].id === selected) {
          newIndex = indexReducer(options.length, Number(index));
          break;
        }
      }

      event?.stopPropagation();
      event?.preventDefault();
      onSelect(options[newIndex].id);
    },
    [selected, onSelect]
  );

  const history = useHistory();

  const selectDefaultOption = useCallback(() => {
    if (
      contentRef.current &&
      !contentRef.current.querySelector(`#${selected}`) &&
      isActive(contentRef)
    ) {
      if (
        defaultSelectedId &&
        contentRef.current.querySelector(`#${defaultSelectedId}`)
      ) {
        onSelect(defaultSelectedId);
      } else {
        updateIndex(() => 0, contentRef.current);
      }
    }
  }, [updateIndex, contentRef, selected, defaultSelectedId, onSelect]);

  useEffect(() => {
    selectDefaultOption();
  }, [selectDefaultOption, currentSearchKey]);

  useEffect(() => {
    if (contentRef.current) {
      const container = contentRef.current;

      const isEscape = (event) => event.keyCode === 27;
      const isNext = (event) => event.keyCode === 40;
      const isPrevious = (event) => event.keyCode === 38;
      const isEnter = (event) => event.keyCode === 13;

      const handleShortcuts = (event) => {
        if (isOpen && isActive(contentRef)) {
          if (isEscape(event)) {
            event.stopPropagation();
            event.preventDefault();
            onClose();
          } else if (isNext(event)) {
            updateIndex(
              (length, index) => (index + 1) % length,
              container,
              event
            );
          } else if (isPrevious(event)) {
            updateIndex(
              (length, index) => (index - 1 + length) % length,
              container,
              event
            );
          } else if (isEnter(event)) {
            if (selected) {
              event.stopPropagation();
              event.preventDefault();
              const value = container.querySelector(`#${selected}`).dataset
                .value;
              if (onOptionChosen) {
                onOptionChosen(value);
              } else {
                history.push(value);
              }
              onClose();
            }
          }
        }
      };

      const handleInputBlur = () => {
        select(null);
      };

      const handleInputFocus = () => {
        selectDefaultOption();
      };

      container.addEventListener("keydown", handleShortcuts);

      const comboBox = container.querySelector('[role="combobox"]');
      comboBox.addEventListener("blur", handleInputBlur);
      comboBox.addEventListener("focus", handleInputFocus);

      return () => {
        container.removeEventListener("keydown", handleShortcuts);
        comboBox.addEventListener("blur", handleInputBlur);
        comboBox.addEventListener("focus", handleInputFocus);
      };
    }
  }, [
    contentRef,
    history,
    isOpen,
    onClose,
    selectDefaultOption,
    selected,
    updateIndex,
    onOptionChosen,
  ]);

  return [selected, onSelect];
};

export default useAutocomplete;
